import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
    Container, Paper, Typography, TextField, Button, IconButton, CircularProgress,
    Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
    InputAdornment, Select, MenuItem, FormControl, InputLabel, Chip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Collapse, Tooltip,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import sortBy from 'lodash/sortBy';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';

import { StockOrderEditData, SupplierCatalogItem } from '../../models';
import {
    addStockOrder,
    bookStockOrder,
    finalizeStockOrder,
    getKoronaSuppliersByBusiness,
    getStockOrderEditData,
    getSupplierCatalog,
    updateStockOrder,
} from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';
import { hasPermission } from '../../../shared/lib/PermissionManager';
import TablePagination from '../../../reports/screens/InventoryOrderReport/TablePagination';
import GlobalStyles from '../../../../styles/GlobalStyles.web';
import {
    CatalogCartItem,
    CatalogOrderSavedSnapshot,
    DEFAULT_CATALOG_ROW_QUANTITY,
    addOrIncrementCatalogCartItem,
    buildCatalogOrderSnapshot,
    catalogOrderHasUnsavedChanges,
    editDataItemToCartItem,
    filterCatalogCartItems,
    formatCatalogCurrency,
    formatCatalogDate,
    formatCatalogNullableNumber,
    getCartItemUnits,
    getCatalogStockOrderRequestData,
    normalizeCatalogQuantity,
    sumCartQuantities,
    sumCartUnits,
} from './helper';

const DEFAULT_CATALOG_PAGE_SIZE = 20;
const CATALOG_SORTABLE_COLUMNS = ['name', 'reorder_code', 'case_qty'] as const;

type PageModeType = 'view_mode' | 'edit_mode';

enum CatalogStockStatusFilter {
    ALL = 'all',
    EXISTING = 'existing',
    NEW = 'new',
}

const CATALOG_STOCK_STATUS_OPTIONS = [
    { value: CatalogStockStatusFilter.ALL, label: 'Show all' },
    { value: CatalogStockStatusFilter.EXISTING, label: 'Show existing' },
    { value: CatalogStockStatusFilter.NEW, label: 'Show new' },
];

const resolveInitialPageMode = (
    hasStockOrderUuid: boolean,
    locationState: { pageMode?: string } | undefined,
): PageModeType => {
    if (!hasStockOrderUuid) {
        return 'edit_mode';
    }
    if (locationState && locationState.pageMode === 'edit_mode') {
        return 'edit_mode';
    }
    return 'view_mode';
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },
        pageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing(2),
            gap: theme.spacing(2),
        },
        pageTitle: {
            marginBottom: 0,
        },
        modeToggle: {
            border: '2px solid currentColor',
            borderRadius: 4,
            padding: 4,
        },
        section: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        filterRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
            flexWrap: 'wrap',
        },
        catalogHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(1),
        },
        catalogToolbar: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(1),
            flexWrap: 'wrap',
        },
        itemsHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(1),
            flexWrap: 'wrap',
        },
        itemsStats: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
        },
        searchField: {
            flex: '1 1 220px',
            minWidth: 180,
        },
        stockStatusSelect: {
            minWidth: 160,
        },
        tableWrap: {
            width: '100%',
            overflowX: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
        },
        table: {
            minWidth: 960,
        },
        qtyInput: {
            width: 80,
        },
        actionCell: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        emptyMsg: {
            padding: theme.spacing(2),
            color: theme.palette.text.secondary,
        },
        submitRow: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginTop: theme.spacing(2),
            flexWrap: 'wrap',
        },
        cartTableWrap: {
            width: '100%',
            overflowX: 'auto',
        },
        loadingOverlay: {
            display: 'flex',
            justifyContent: 'center',
            padding: theme.spacing(3),
        },
    }),
);

const sortingToOrdering = (sorting: SortingState): string => {
    if (!sorting.length) {
        return 'name';
    }
    const { id, desc } = sorting[0];
    const field = CATALOG_SORTABLE_COLUMNS.includes(id as typeof CATALOG_SORTABLE_COLUMNS[number])
        ? id
        : 'name';
    return desc ? `-${field}` : field;
};

// Universal Catalog Ordering (FR-01/FR-02): lets store staff build or edit a
// stock order by browsing a supplier's master catalog. Submission goes through
// the async catalog-order task (create or update). Booking is explicit.
const CreateCatalogOrderScreen = (props: any) => {
    const classes = useStyles({});
    const stockOrderUuid = props.match && props.match.params
        ? props.match.params.stockOrderUuid
        : undefined;
    // isEditMode = existing order (has UUID). Distinct from pageMode edit/view toggle.
    const isEditMode = Boolean(stockOrderUuid);

    const [pageMode, setPageMode] = useState<{ type: PageModeType }>(() => ({
        type: resolveInitialPageMode(
            Boolean(stockOrderUuid),
            props.location && props.location.state,
        ),
    }));
    const [catalogExpanded, setCatalogExpanded] = useState(() => {
        if (!stockOrderUuid) {
            return true;
        }
        return resolveInitialPageMode(
            Boolean(stockOrderUuid),
            props.location && props.location.state,
        ) === 'edit_mode';
    });

    const [linkedSuppliers, setLinkedSuppliers] = useState<any[]>([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
    const [description, setDescription] = useState('');
    const [editOrderNumber, setEditOrderNumber] = useState<string | null>(null);
    const [editBookingTime, setEditBookingTime] = useState<string | null>(null);
    const [editSupplierUuid, setEditSupplierUuid] = useState<string | null>(null);
    const [editSupplierName, setEditSupplierName] = useState<string | null>(null);
    const [editLocation, setEditLocation] = useState<{
        id: number;
        korona_org_unit: string;
        name?: string | null;
    } | null>(null);
    const [editLoading, setEditLoading] = useState(false);
    const [skipNextSupplierReset, setSkipNextSupplierReset] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [itemsSearchTerm, setItemsSearchTerm] = useState('');
    const [stockStatusFilter, setStockStatusFilter] = useState(CatalogStockStatusFilter.ALL);
    const [catalogRows, setCatalogRows] = useState<SupplierCatalogItem[]>([]);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogCount, setCatalogCount] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_CATALOG_PAGE_SIZE);
    const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
    const [rowQuantities, setRowQuantities] = useState<Record<string, number>>({});

    const [cartItems, setCartItems] = useState<CatalogCartItem[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [submittedTaskId, setSubmittedTaskId] = useState<number | null>(null);
    const [canBookAfterSave, setCanBookAfterSave] = useState(false);
    const [bookDialogOpen, setBookDialogOpen] = useState(false);
    const [isBooking, setIsBooking] = useState(false);
    const [editFinalizedAt, setEditFinalizedAt] = useState<string | null>(null);
    const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [savedSnapshot, setSavedSnapshot] = useState<CatalogOrderSavedSnapshot | null>(null);
    const [unsavedActionDialogOpen, setUnsavedActionDialogOpen] = useState(false);
    const [pendingWorkflowAction, setPendingWorkflowAction] = useState<'finalize' | 'book' | null>(null);

    const authUser = props.userData?.userData?.userData;
    const isSuperUser = authUser?.is_superuser;
    const userPermissions = authUser?.user_permissions || [];
    const canFinalize = hasPermission(userPermissions, 'finalize_stockorder', isSuperUser);
    const canBook = hasPermission(userPermissions, 'book_stockorder', isSuperUser);

    const selectedSupplier = linkedSuppliers.find((s) => String(s.uuid) === String(selectedSupplierId)) || null;
    const selectedLocations = (props.userData && props.userData.selectedLocations) || [];
    const pickerLocation = selectedLocations.length === 1 ? selectedLocations[0] : null;
    // Existing-order mode uses the order's locked location; create requires exactly one picker selection.
    const selectedLocation = isEditMode ? (editLocation || pickerLocation) : pickerLocation;
    // Browse catalog by Korona supplier uuid — selected supplier preferred, edit-data fallback.
    const catalogSupplierUuid = (
        (selectedSupplier && selectedSupplier.uuid)
            ? String(selectedSupplier.uuid)
            : editSupplierUuid
    );
    const businessId = props.userData && props.userData.selectedBusiness && props.userData.selectedBusiness.business_id;
    const isBooked = Boolean(editBookingTime);
    const isFinalized = Boolean(editFinalizedAt);
    const formDisabled = isBooked || isFinalized;
    const isPageEditMode = () => pageMode.type === 'edit_mode';
    const canMutate = isPageEditMode() && !formDisabled;
    const showPageModeToggle = isEditMode && !formDisabled;

    const filteredCartItems = filterCatalogCartItems(cartItems, itemsSearchTerm);
    const totalCases = sumCartQuantities(cartItems);
    const totalUnits = sumCartUnits(cartItems);
    const hasItemsSearch = Boolean(itemsSearchTerm.trim());
    const hasUnsavedChanges = catalogOrderHasUnsavedChanges(savedSnapshot, description, cartItems);

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    const openWorkflowConfirm = (action: 'finalize' | 'book') => {
        if (action === 'finalize') {
            setFinalizeDialogOpen(true);
        } else {
            setBookDialogOpen(true);
        }
    };

    const requestFinalizeOrBook = (action: 'finalize' | 'book') => {
        if (!hasUnsavedChanges) {
            openWorkflowConfirm(action);
            return;
        }
        setPendingWorkflowAction(action);
        setUnsavedActionDialogOpen(true);
    };

    const handleDiscardAndContinue = () => {
        if (!savedSnapshot || !pendingWorkflowAction) {
            setUnsavedActionDialogOpen(false);
            setPendingWorkflowAction(null);
            return;
        }
        setDescription(savedSnapshot.description);
        setCartItems(savedSnapshot.cartItems.map((item) => ({ ...item })));
        setCanBookAfterSave(Boolean(savedSnapshot) && !isBooked);
        setUnsavedActionDialogOpen(false);
        const action = pendingWorkflowAction;
        setPendingWorkflowAction(null);
        openWorkflowConfirm(action);
    };

    const togglePageMode = () => {
        if (formDisabled) {
            return;
        }
        if (pageMode.type === 'view_mode') {
            setPageMode({ type: 'edit_mode' });
            setCatalogExpanded(true);
        } else {
            setPageMode({ type: 'view_mode' });
            setCatalogExpanded(false);
        }
    };

    const getPageTitle = () => {
        if (!isEditMode) {
            return 'Create Stock Order';
        }
        const numberSuffix = editOrderNumber ? ` #${editOrderNumber}` : '';
        if (isPageEditMode() && !formDisabled) {
            return `Edit Catalog Order${numberSuffix}`;
        }
        return `View Catalog Order${numberSuffix}`;
    };

    useEffect(() => {
        if (!businessId) {
            return;
        }
        getKoronaSuppliersByBusiness(businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                const suppliers = (data.results || []).filter((s: any) => !s.deleted);
                setLinkedSuppliers(sortBy(suppliers, 'name'));
            })
            .catch(() => {
                setLinkedSuppliers([]);
            });
    }, [businessId]);

    useEffect(() => {
        if (!isEditMode || !stockOrderUuid || !businessId) {
            return;
        }
        setEditLoading(true);
        getStockOrderEditData(stockOrderUuid, businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: StockOrderEditData) => {
                setEditOrderNumber(data.number);
                setEditBookingTime(data.bookingTime);
                setEditFinalizedAt((data as any).finalizedAt || null);
                setDescription(data.description || '');
                const nextCart = (data.items || []).map(editDataItemToCartItem);
                setCartItems(nextCart);
                setSavedSnapshot(buildCatalogOrderSnapshot(data.description || '', nextCart));
                setCanBookAfterSave(!data.bookingTime && !data.has_in_flight_task);
                setEditSupplierUuid(
                    data.supplier && data.supplier.uuid ? String(data.supplier.uuid) : null,
                );
                setEditSupplierName(
                    data.supplier && data.supplier.name ? String(data.supplier.name) : null,
                );
                setEditLocation(data.location || null);
                setSkipNextSupplierReset(true);
                setEditLoading(false);
                if (data.bookingTime || (data as any).finalizedAt) {
                    setPageMode({ type: 'view_mode' });
                    setCatalogExpanded(false);
                }
            })
            .catch((error: any) => {
                setEditLoading(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to load stock order for editing.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    }, [isEditMode, stockOrderUuid, businessId]);

    useEffect(() => {
        if (!editSupplierUuid || linkedSuppliers.length === 0) {
            return;
        }
        const match = linkedSuppliers.find(
            (s) => String(s.uuid) === String(editSupplierUuid),
        );
        if (match && String(match.uuid) !== String(selectedSupplierId)) {
            setSkipNextSupplierReset(true);
            setSelectedSupplierId(String(match.uuid));
        }
    }, [editSupplierUuid, linkedSuppliers]);

    // Reset the in-progress cart/browse state whenever the supplier changes
    // (create mode only; existing-order mode preloads cart and locks supplier).
    useEffect(() => {
        if (skipNextSupplierReset) {
            setSkipNextSupplierReset(false);
            return;
        }
        if (isEditMode) {
            return;
        }
        setCartItems([]);
        setCatalogRows([]);
        setCatalogCount(0);
        setSearchTerm('');
        setDebouncedSearch('');
        setItemsSearchTerm('');
        setStockStatusFilter(CatalogStockStatusFilter.ALL);
        setPageIndex(0);
        setPageSize(DEFAULT_CATALOG_PAGE_SIZE);
        setSorting([{ id: 'name', desc: false }]);
        setRowQuantities({});
        setSubmittedTaskId(null);
        setCanBookAfterSave(false);
    }, [selectedSupplierId]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
            setPageIndex(0);
        }, 400);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    useEffect(() => {
        if (!businessId || !catalogSupplierUuid || !selectedLocation || !selectedLocation.korona_org_unit) {
            setCatalogRows([]);
            setCatalogCount(0);
            return undefined;
        }
        let active = true;
        setCatalogLoading(true);
        const ordering = sortingToOrdering(sorting);
        getSupplierCatalog(
            businessId,
            catalogSupplierUuid,
            ordering,
            selectedLocation.id,
            selectedLocation.korona_org_unit,
            debouncedSearch || undefined,
            pageIndex + 1,
            pageSize,
            stockStatusFilter,
        )
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                if (active) {
                    setCatalogRows(data.results || []);
                    setCatalogCount(data.count || 0);
                    setCatalogLoading(false);
                }
            })
            .catch(() => {
                if (active) {
                    setCatalogRows([]);
                    setCatalogCount(0);
                    setCatalogLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [
        businessId,
        catalogSupplierUuid,
        selectedLocation,
        debouncedSearch,
        pageIndex,
        pageSize,
        sorting,
        stockStatusFilter,
    ]);

    const getRowQuantity = (catalogItem: SupplierCatalogItem) => {
        const key = String(catalogItem.id);
        if (rowQuantities[key] != null) {
            return rowQuantities[key];
        }
        return DEFAULT_CATALOG_ROW_QUANTITY;
    };

    const handleRowQuantityChange = (catalogItem: SupplierCatalogItem, value: unknown) => {
        const key = String(catalogItem.id);
        const parsed = Number(value);
        setRowQuantities((prev) => ({
            ...prev,
            [key]: Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0,
        }));
    };

    const handleAddToCart = (catalogItem: SupplierCatalogItem) => {
        if (!canMutate) {
            return;
        }
        const quantity = normalizeCatalogQuantity(getRowQuantity(catalogItem));
        setCartItems((prev) => addOrIncrementCatalogCartItem(prev, catalogItem, quantity));
        setRowQuantities((prev) => ({
            ...prev,
            [String(catalogItem.id)]: DEFAULT_CATALOG_ROW_QUANTITY,
        }));
        setCanBookAfterSave(false);
    };

    const handleQuantityChange = (key: string, quantity: number) => {
        if (!canMutate) {
            return;
        }
        setCartItems((prev) => prev.map((c) => (
            c.key === key ? { ...c, quantity: normalizeCatalogQuantity(quantity, c.quantity) } : c
        )));
        setCanBookAfterSave(false);
    };

    const handleRemoveFromCart = (key: string) => {
        if (!canMutate) {
            return;
        }
        setCartItems((prev) => prev.filter((c) => c.key !== key));
        setCanBookAfterSave(false);
    };

    const handleSubmit = (options?: { onSuccess?: () => void }) => {
        const supplierForPayload = selectedSupplier || (
            catalogSupplierUuid
                ? { uuid: catalogSupplierUuid, name: editSupplierName }
                : null
        );
        if (!supplierForPayload || !selectedLocation || cartItems.length === 0 || !canMutate) {
            return;
        }
        const invalidQty = cartItems.some((c) => !c.quantity || c.quantity <= 0);
        if (invalidQty) {
            showToast('Every line needs a quantity greater than 0.');
            return;
        }

        const payload = getCatalogStockOrderRequestData(
            supplierForPayload,
            selectedLocation,
            description,
            cartItems,
            { includeProductUuids: isEditMode },
        );
        setSubmitting(true);
        const request = isEditMode && stockOrderUuid
            ? updateStockOrder(stockOrderUuid, payload, businessId)
            : addStockOrder(payload);

        request
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setSubmitting(false);
                setSubmittedTaskId(data && data.task_id ? data.task_id : null);
                setCanBookAfterSave(true);
                setSavedSnapshot(buildCatalogOrderSnapshot(description, cartItems));
                if (!isEditMode) {
                    setCartItems([]);
                    setDescription('');
                    setSavedSnapshot(buildCatalogOrderSnapshot('', []));
                }
                showToast(
                    isEditMode
                        ? 'Stock order changes submitted and are now processing.'
                        : 'Stock order submitted and is now processing.',
                    ToastSuccess,
                );
                if (options && options.onSuccess) {
                    options.onSuccess();
                }
            })
            .catch((error: any) => {
                setSubmitting(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : (isEditMode ? 'Failed to update stock order.' : 'Failed to submit stock order.');
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const handleSaveAndContinue = () => {
        const action = pendingWorkflowAction;
        if (!action) {
            setUnsavedActionDialogOpen(false);
            return;
        }
        setUnsavedActionDialogOpen(false);
        setPendingWorkflowAction(null);
        handleSubmit({
            onSuccess: () => openWorkflowConfirm(action),
        });
    };

    const handleFinalizeConfirm = () => {
        const uuid = stockOrderUuid;
        if (!uuid) {
            return;
        }
        setIsFinalizing(true);
        finalizeStockOrder(uuid, businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setIsFinalizing(false);
                setFinalizeDialogOpen(false);
                setEditFinalizedAt(
                    data && data.finalizedAt ? data.finalizedAt : new Date().toISOString(),
                );
                if (data && data.bookingTime) {
                    setEditBookingTime(data.bookingTime);
                }
                setCanBookAfterSave(false);
                setPageMode({ type: 'view_mode' });
                setCatalogExpanded(false);
                showToast('Stock order finalized successfully.', ToastSuccess);
                props.history.push(BusinessRouterPaths.stockOrderList);
            })
            .catch((error: any) => {
                setIsFinalizing(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to finalize stock order.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const handleBookConfirm = () => {
        const uuid = stockOrderUuid;
        if (!uuid) {
            return;
        }
        setIsBooking(true);
        bookStockOrder(uuid, businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setIsBooking(false);
                setBookDialogOpen(false);
                setEditBookingTime(data && data.bookingTime ? data.bookingTime : new Date().toISOString());
                setCanBookAfterSave(false);
                setPageMode({ type: 'view_mode' });
                setCatalogExpanded(false);
                showToast('Stock order booked successfully.', ToastSuccess);
                props.history.push(BusinessRouterPaths.stockOrderList);
            })
            .catch((error: any) => {
                setIsBooking(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to book stock order.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const columns: ColumnDef<SupplierCatalogItem>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            enableSorting: true,
            cell: (info) => info.getValue() as string,
        },
        {
            accessorKey: 'reorder_code',
            header: 'Reorder Code',
            enableSorting: true,
            cell: (info) => (info.getValue() as string) || '-',
        },
        {
            accessorKey: 'case_qty',
            header: 'Case Qty',
            enableSorting: true,
            cell: (info) => {
                const value = info.getValue() as number | null;
                return value != null ? String(value) : '-';
            },
        },
        {
            id: 'status',
            header: 'Status',
            enableSorting: false,
            cell: ({ row }) => (
                row.original.existing_product_uuid ? (
                    <Chip label="Existing" size="small" />
                ) : (
                    <Chip label="New" size="small" color="primary" />
                )
            ),
        },
        {
            id: 'current_stock',
            header: 'Current Stock',
            enableSorting: false,
            cell: ({ row }) => formatCatalogNullableNumber(row.original.current_stock),
        },
        {
            id: 'last_purchase_price',
            header: 'Last Purchase Price',
            enableSorting: false,
            cell: ({ row }) => formatCatalogCurrency(row.original.last_purchase_price),
        },
        {
            id: 'last_order_date',
            header: 'Last Order Date',
            enableSorting: false,
            cell: ({ row }) => formatCatalogDate(row.original.last_order_date),
        },
        {
            id: 'actions',
            header: '',
            enableSorting: false,
            cell: ({ row }) => (
                <div className={classes.actionCell}>
                    <TextField
                        className={classes.qtyInput}
                        type="number"
                        size="small"
                        variant="outlined"
                        label="Cases"
                        value={getRowQuantity(row.original)}
                        inputProps={{ min: 1, step: 1 }}
                        disabled={!canMutate}
                        onChange={(e) => handleRowQuantityChange(row.original, e.target.value)}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        disabled={!canMutate}
                        onClick={() => handleAddToCart(row.original)}
                    >
                        Add
                    </Button>
                </div>
            ),
        },
    ];

    const table = useReactTable({
        data: catalogRows,
        columns,
        pageCount: Math.max(1, Math.ceil(catalogCount / pageSize)),
        state: {
            sorting,
            pagination: {
                pageIndex,
                pageSize,
            },
        },
        manualPagination: true,
        manualSorting: true,
        onSortingChange: (updater) => {
            setSorting((prev) => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                return next.length ? next.slice(0, 1) : [{ id: 'name', desc: false }];
            });
            setPageIndex(0);
        },
        onPaginationChange: (updater) => {
            const next = typeof updater === 'function'
                ? updater({ pageIndex, pageSize })
                : updater;
            setPageIndex(next.pageIndex);
            setPageSize(next.pageSize);
        },
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.id),
    });

    const renderLocationWarning = () => {
        if (isEditMode) {
            if (editLocation && editLocation.name) {
                return (
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Location (locked): {editLocation.name}
                    </Typography>
                );
            }
            return null;
        }
        if (selectedLocations.length === 1) {
            return null;
        }
        const msg = selectedLocations.length === 0
            ? 'Select exactly one location from the location picker above to create a catalog order.'
            : 'Multiple locations are selected. Select exactly one location from the location picker above to create a catalog order.';
        return (
            <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                {msg}
            </Typography>
        );
    };

    const renderBrowseCatalogBody = () => {
        if (!catalogSupplierUuid) {
            return (
                <Typography color="textSecondary">
                    {isEditMode
                        ? 'This stock order has no supplier; catalog browse is unavailable.'
                        : 'Select a supplier to browse its master catalog.'}
                </Typography>
            );
        }
        if (!selectedLocation || !selectedLocation.korona_org_unit) {
            return (
                <Typography color="textSecondary">
                    {isEditMode
                        ? 'This stock order location could not be resolved for catalog browse.'
                        : 'Select exactly one location to browse the catalog with stock and order history.'}
                </Typography>
            );
        }

        return (
            <>
                <div className={classes.catalogToolbar}>
                    <TextField
                        className={classes.searchField}
                        variant="outlined"
                        size="small"
                        placeholder="Search by product name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        disabled={!canMutate}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchTerm('')}
                                        disabled={!canMutate}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        }}
                    />
                    <FormControl variant="outlined" size="small" className={classes.stockStatusSelect}>
                        <InputLabel id="catalog-stock-status-label">Show</InputLabel>
                        <Select
                            labelId="catalog-stock-status-label"
                            value={stockStatusFilter}
                            label="Show"
                            disabled={!canMutate}
                            onChange={(e) => {
                                setStockStatusFilter(e.target.value as CatalogStockStatusFilter);
                                setPageIndex(0);
                            }}
                        >
                            {CATALOG_STOCK_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.tableWrap}>
                    {catalogLoading ? (
                        <div className={classes.loadingOverlay}>
                            <CircularProgress size={24} />
                        </div>
                    ) : (
                        <Table size="small" className={classes.table}>
                            <TableHead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableCell
                                                    key={header.id}
                                                    sortDirection={sorted === 'asc' ? 'asc' : sorted === 'desc' ? 'desc' : false}
                                                >
                                                    {canSort ? (
                                                        <TableSortLabel
                                                            active={!!sorted}
                                                            direction={sorted === 'desc' ? 'desc' : 'asc'}
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                        </TableSortLabel>
                                                    ) : (
                                                        flexRender(header.column.columnDef.header, header.getContext())
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody>
                                {table.getRowModel().rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length}>
                                            <Typography className={classes.emptyMsg}>
                                                {debouncedSearch
                                                    ? `No catalog items found for "${debouncedSearch}".`
                                                    : 'No catalog items for this supplier.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id} hover>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                    <TablePagination
                        page={pageIndex}
                        pageSize={pageSize}
                        totalCount={catalogCount}
                        onPageChange={setPageIndex}
                        onPageSizeChange={(nextSize) => {
                            setPageSize(nextSize);
                            setPageIndex(0);
                        }}
                    />
                </div>
            </>
        );
    };

    const renderBrowseCatalog = () => (
        <>
            <div className={classes.catalogHeaderRow}>
                <Typography variant="subtitle1">Browse Catalog</Typography>
                <Button
                    size="small"
                    onClick={() => setCatalogExpanded((prev) => !prev)}
                    endIcon={catalogExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {catalogExpanded ? 'Collapse' : 'Expand'}
                </Button>
            </div>
            <Collapse in={catalogExpanded}>
                {renderBrowseCatalogBody()}
            </Collapse>
        </>
    );

    const renderItemsSection = () => {
        const linesLabel = hasItemsSearch
            ? `${filteredCartItems.length} of ${cartItems.length} lines`
            : `${cartItems.length} lines`;

        return (
            <Paper className={classes.section}>
                <div className={classes.itemsHeaderRow}>
                    <Typography variant="subtitle1">Items</Typography>
                    <div className={classes.itemsStats}>
                        <Chip size="small" label={linesLabel} />
                        <Chip size="small" label={`Total cases: ${totalCases}`} />
                        <Chip size="small" label={`Total units: ${totalUnits}`} />
                    </div>
                </div>
                {cartItems.length > 0 && (
                    <div className={classes.catalogToolbar}>
                        <TextField
                            className={classes.searchField}
                            variant="outlined"
                            size="small"
                            placeholder="Search items by name or reorder code"
                            value={itemsSearchTerm}
                            onChange={(e) => setItemsSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: itemsSearchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setItemsSearchTerm('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                    </div>
                )}
                {cartItems.length === 0 ? (
                    <Typography color="textSecondary">
                        No items added yet. Browse the catalog above and add items.
                    </Typography>
                ) : filteredCartItems.length === 0 ? (
                    <Typography color="textSecondary">
                        No items match your search.{' '}
                        <Button size="small" color="primary" onClick={() => setItemsSearchTerm('')}>
                            Clear search
                        </Button>
                    </Typography>
                ) : (
                    <div className={classes.cartTableWrap}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Reorder Code</TableCell>
                                    <TableCell>Case Qty</TableCell>
                                    <TableCell>Cases</TableCell>
                                    <TableCell>Units</TableCell>
                                    {canMutate ? <TableCell /> : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCartItems.map((item) => (
                                    <TableRow key={item.key}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.reorder_code || '-'}</TableCell>
                                        <TableCell>{item.case_qty || '-'}</TableCell>
                                        <TableCell>
                                            {canMutate ? (
                                                <TextField
                                                    className={classes.qtyInput}
                                                    type="number"
                                                    size="small"
                                                    variant="outlined"
                                                    value={item.quantity}
                                                    inputProps={{ min: 1 }}
                                                    onChange={(e) => handleQuantityChange(
                                                        item.key,
                                                        Number(e.target.value),
                                                    )}
                                                />
                                            ) : (
                                                item.quantity
                                            )}
                                        </TableCell>
                                        <TableCell>{getCartItemUnits(item)}</TableCell>
                                        {canMutate ? (
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveFromCart(item.key)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {canMutate && (
                    <div className={classes.submitRow}>
                        {submittedTaskId && (
                            <Button
                                size="small"
                                onClick={() => props.history.push(BusinessRouterPaths.catalogOrderTaskHistory)}
                            >
                                View task #{submittedTaskId} status
                            </Button>
                        )}
                        {isEditMode && canFinalize && (
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={isBooked || isFinalized || isFinalizing || isBooking || submitting}
                                onClick={() => requestFinalizeOrBook('finalize')}
                            >
                                Finalize Order
                            </Button>
                        )}
                        {isEditMode && canBook && (
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={
                                    isBooked
                                    || isBooking
                                    || submitting
                                    || (!hasUnsavedChanges && !canBookAfterSave)
                                }
                                onClick={() => requestFinalizeOrBook('book')}
                            >
                                Book Order
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                !catalogSupplierUuid
                                || !selectedLocation
                                || cartItems.length === 0
                                || submitting
                            }
                            onClick={() => handleSubmit()}
                        >
                            {submitting
                                ? <CircularProgress size={20} />
                                : (isEditMode ? 'Save Changes' : 'Submit Order')}
                        </Button>
                    </div>
                )}
                {!canMutate && submittedTaskId && (
                    <div className={classes.submitRow}>
                        <Button
                            size="small"
                            onClick={() => props.history.push(BusinessRouterPaths.catalogOrderTaskHistory)}
                        >
                            View task #{submittedTaskId} status
                        </Button>
                    </div>
                )}
            </Paper>
        );
    };

    if (editLoading) {
        return (
            <Container maxWidth={false} className={classes.root}>
                <div className={classes.loadingOverlay}>
                    <CircularProgress />
                </div>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} className={classes.root}>
            <div className={classes.pageHeader}>
                <Typography variant="h6" className={classes.pageTitle}>
                    {getPageTitle()}
                </Typography>
                {showPageModeToggle && (
                    <Tooltip title="Toggle Page Mode">
                        <IconButton
                            className={classes.modeToggle}
                            style={{
                                color: isPageEditMode() ? '#ffb300' : GlobalStyles.primaryColor,
                            }}
                            onClick={togglePageMode}
                            aria-label="Toggle page mode"
                        >
                            {isPageEditMode() ? <EditIcon /> : <VisibilityIcon />}
                        </IconButton>
                    </Tooltip>
                )}
            </div>

            {isBooked && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                    This stock order is booked and can no longer be edited.
                </Typography>
            )}

            {renderLocationWarning()}

            <Paper className={classes.section}>
                <div className={classes.filterRow}>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
                        <InputLabel id="catalog-order-supplier-label">Supplier</InputLabel>
                        <Select
                            labelId="catalog-order-supplier-label"
                            value={
                                selectedSupplierId
                                || (isEditMode && editSupplierUuid ? '__edit_supplier__' : '')
                            }
                            onChange={(e) => setSelectedSupplierId(e.target.value as string)}
                            label="Supplier"
                            disabled={isEditMode || !canMutate}
                        >
                            {isEditMode && editSupplierUuid && !selectedSupplierId && (
                                <MenuItem value="__edit_supplier__">
                                    {editSupplierName || editSupplierUuid}
                                </MenuItem>
                            )}
                            {linkedSuppliers.map((s) => (
                                <MenuItem key={s.uuid} value={s.uuid}>{s.name || s.uuid}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Description"
                        variant="outlined"
                        size="small"
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value);
                            setCanBookAfterSave(false);
                        }}
                        disabled={!canMutate}
                        style={{ minWidth: 260 }}
                    />
                </div>

                {renderBrowseCatalog()}
            </Paper>

            {renderItemsSection()}

            <Dialog open={finalizeDialogOpen} onClose={() => !isFinalizing && setFinalizeDialogOpen(false)}>
                <DialogTitle>Finalize stock order</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {editOrderNumber
                            ? `Finalize stock order #${editOrderNumber}? Depending on this business's workflow this may also notify the supplier and book the order. Editing is locked after finalizing.`
                            : "Finalize this stock order? Depending on this business's workflow this may also notify the supplier and book the order."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizeDialogOpen(false)} disabled={isFinalizing}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleFinalizeConfirm}
                        disabled={isFinalizing}
                    >
                        {isFinalizing ? <CircularProgress size={20} /> : 'Finalize'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={bookDialogOpen} onClose={() => !isBooking && setBookDialogOpen(false)}>
                <DialogTitle>Book stock order</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {editOrderNumber
                            ? `Book stock order #${editOrderNumber}? Booking is final from this screen.`
                            : 'Book this stock order? Booking is final from this screen.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBookDialogOpen(false)} disabled={isBooking}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleBookConfirm}
                        disabled={isBooking}
                    >
                        {isBooking ? <CircularProgress size={20} /> : 'Book'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={unsavedActionDialogOpen}
                onClose={() => {
                    if (submitting) {
                        return;
                    }
                    setUnsavedActionDialogOpen(false);
                    setPendingWorkflowAction(null);
                }}
            >
                <DialogTitle>Unsaved changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have unsaved changes. Save or discard them before
                        {pendingWorkflowAction === 'book' ? ' booking' : ' finalizing'}.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setUnsavedActionDialogOpen(false);
                            setPendingWorkflowAction(null);
                        }}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleDiscardAndContinue} disabled={submitting || !savedSnapshot}>
                        Discard &amp; continue
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSaveAndContinue}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={20} /> : 'Save & continue'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateCatalogOrderScreen);
